
/**
 * Hooktheme Quick View Component
 * Extends the existing ModalDialog pattern with AJAX content loading
 *
 * Usage:
 * <hooktheme-quick-view id="QuickView-123-section">
 *   <div role="dialog" class="hooktheme-quick-view__content">
 *     <button data-modal-close>Close</button>
 *     <div id="QuickViewInfo-123-section" class="hooktheme-quick-view__content-info"></div>
 *   </div>
 * </hooktheme-quick-view>
 *
 * Triggered by:
 * <modal-opener data-modal="#QuickView-123-section">
 *   <button data-product-url="product-handle">Quick View</button>
 * </modal-opener>
 */

// Prevent duplicate class declaration when script is loaded multiple times
if (!customElements.get('hooktheme-quick-view')) {

class HookthemeQuickView extends HTMLElement {
  constructor() {
    super();
    this.bindEvents();
  }

  bindEvents() {
    // Close button handling
    this.querySelectorAll('[data-modal-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.hide());
    });

    // ESC key to close
    this.addEventListener('keyup', (e) => {
      if (e.key === 'Escape') this.hide();
    });

    // Click overlay to close
    this.addEventListener('click', (e) => {
      if (e.target === this) this.hide();
    });
  }

  connectedCallback() {
    // Move to body for proper z-index stacking
    if (!this.moved) {
      this.moved = true;
      document.body.appendChild(this);
    }
  }

  /**
   * Get the container element for product content
   */
  getContentContainer() {
    if (!this.contentContainer) {
      this.contentContainer = this.querySelector('[id^="QuickViewInfo-"]');
    }
    return this.contentContainer;
  }

  /**
   * Show modal and fetch product content
   * Called by modal-opener via: modal.show(button)
   * @param {HTMLElement} opener
   */
  show(opener) {
    this.openedBy = opener;

    const productUrl = opener.getAttribute('data-product-url');

    if (!productUrl) {
      console.error('[HookthemeQuickView] Missing data-product-url on trigger');
      return;
    }

    this.setLoading(opener, true);

    this.fetchProductContent(productUrl)
      .then((productHTML) => {
        this.renderProduct(productHTML);
        this.initializeFeatures();
        this.openModal();
      })
      .catch((error) => {
        console.error('[HookthemeQuickView] Failed to load product:', error);
      })
      .finally(() => {
        this.setLoading(opener, false);
      });
  }

  /**
   * Open the modal after content is loaded
   */
  openModal() {
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    document.body.style.paddingRight = scrollbarWidth + 'px';
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');

    if (typeof trapFocus === 'function') {
      trapFocus(this, this.querySelector('[role="dialog"]'));
    }

    if (typeof window.pauseAllMedia === 'function') {
      window.pauseAllMedia();
    }
  }

  /**
   * Hide modal and clear content
   */
  hide() {
    document.body.style.paddingRight = '';
    document.body.classList.remove('overflow-hidden');
    this.removeAttribute('open');

    const container = this.getContentContainer();

    if (container) {
      container.innerHTML = '';
    }

    document
      .querySelector('.product__card.quick--view-opened')
      ?.classList.remove('quick--view-opened');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.openedBy);
    }

    if (typeof window.pauseAllMedia === 'function') {
      window.pauseAllMedia();
    }
  }

  /**
   * Fetch localized product content via AJAX
   * Uses the current Shopify storefront language.
   *
   * @param {string} productUrl
   * @returns {Promise<Document>}
   */
  
async fetchProductContent(productUrl) {
  // Get the correct language-aware storefront root from Shopify.
  // Default Hebrew: /
  // Arabic: /ar/
  // Russian: /ru/
  // English: /en/

  const shopifyRoot = window.Shopify?.routes?.root;

  const pathLocale = window.location.pathname.match(
    /^\/(he|en|ar|ru)(?=\/|$)/i
  );

  const fallbackRoot = pathLocale
    ? `/${pathLocale[1].toLowerCase()}/`
    : '/';

  let root = (
    typeof shopifyRoot === 'string' &&
    shopifyRoot.startsWith('/')
  )
    ? shopifyRoot
    : fallbackRoot;

  if (!root.endsWith('/')) {
    root += '/';
  }

  const productHandle = String(productUrl)
    .split('?')[0]
    .replace(/\/$/, '')
    .split('/')
    .filter(Boolean)
    .pop();

  if (!productHandle) {
    throw new Error('[Quick View] Missing product handle');
  }

  const requestUrl =
    `${root}products/${encodeURIComponent(productHandle)}?view=quickview`;

  const response = await fetch(requestUrl, {
    credentials: 'same-origin'
  });

  if (!response.ok) {
    throw new Error(
      `[Quick View] HTTP ${response.status}: ${requestUrl}`
    );
  }

  const html = await response.text();

  return new DOMParser().parseFromString(
    html,
    'text/html'
  );
}


  /**
   * Render product content into modal
   * @param {Document} responseHTML
   */
  renderProduct(responseHTML) {
    const productElement =
      responseHTML.querySelector('.quick_view__body');

    if (!productElement) {
      console.error(
        '[HookthemeQuickView] .quick_view__body not found in response'
      );
      return;
    }

    const container = this.getContentContainer();

    this.injectStylesheets(responseHTML);
    this.applyColorScheme(productElement, container);
    this.rewriteElementIds(productElement);
    this.injectContent(container, productElement.outerHTML);
  }

  /**
   * Inject stylesheets that aren't already loaded
   */
  injectStylesheets(responseHTML) {
    responseHTML
      .querySelectorAll('link[rel="stylesheet"]')
      .forEach((link) => {
        const href = link.getAttribute('href');

        if (href && !document.querySelector(`link[href="${href}"]`)) {
          document.head.appendChild(link.cloneNode(true));
        }
      });

    responseHTML.querySelectorAll('style').forEach((style) => {
      if (!document.querySelector('style[data-quick-view-styles]')) {
        const newStyle = style.cloneNode(true);
        newStyle.setAttribute('data-quick-view-styles', 'true');
        document.head.appendChild(newStyle);
      }
    });
  }

  /**
   * Apply color scheme from response to container
   */
  applyColorScheme(productElement, container) {
    const colorScheme =
      productElement.querySelector('[class*="color-"]');

    if (colorScheme) {
      const colorClass = Array.from(colorScheme.classList).find(
        (cls) => cls.startsWith('color-')
      );

      if (colorClass) {
        container.classList.add(colorClass);
      }
    }
  }

  /**
   * Rewrite element IDs to prevent duplicates
   * while preserving internal relationships.
   */
  rewriteElementIds(productElement) {
    const uniquePrefix = `qv-${Date.now()}`;

    const sectionElement =
      productElement.querySelector('[data-section]');

    const originalSectionId = sectionElement?.dataset.section;

    if (!originalSectionId) return;

    // Store original section on variant pickers
    productElement
      .querySelectorAll('hooktheme-variant-picker')
      .forEach((picker) => {
        picker.dataset.originalSection = originalSectionId;
        picker.dataset.quickViewSection = uniquePrefix;
      });

    // Update form IDs
    productElement
      .querySelectorAll('form[id]')
      .forEach((form) => {
        const formId = form.getAttribute('id');

        if (
          formId &&
          typeof formId === 'string' &&
          formId.includes(originalSectionId)
        ) {
          form.setAttribute(
            'id',
            formId.replace(originalSectionId, uniquePrefix)
          );
        }
      });

    // Update form references
    productElement
      .querySelectorAll('[form]')
      .forEach((el) => {
        const formAttr = el.getAttribute('form');

        if (
          formAttr &&
          formAttr.includes(originalSectionId)
        ) {
          el.setAttribute(
            'form',
            formAttr.replace(originalSectionId, uniquePrefix)
          );
        }
      });

    // Update price container IDs
    productElement
      .querySelectorAll('[id^="price-"]')
      .forEach((el) => {
        const elId = el.getAttribute('id');

        if (elId && elId.includes(originalSectionId)) {
          el.setAttribute(
            'id',
            elId.replace(originalSectionId, uniquePrefix)
          );
        }
      });

    // Update quantity input IDs
    productElement
      .querySelectorAll('[id^="Quantity-"]')
      .forEach((el) => {
        const elId = el.getAttribute('id');

        if (elId && elId.includes(originalSectionId)) {
          el.setAttribute(
            'id',
            elId.replace(originalSectionId, uniquePrefix)
          );
        }
      });

    // Update option select IDs
    productElement
      .querySelectorAll('[id^="Option-"]')
      .forEach((el) => {
        const elId = el.getAttribute('id');

        if (elId && elId.includes(originalSectionId)) {
          el.setAttribute(
            'id',
            elId.replace(originalSectionId, uniquePrefix)
          );
        }
      });

    // Update labels referring to option IDs
    productElement
      .querySelectorAll('[for^="Option-"]')
      .forEach((el) => {
        const forAttr = el.getAttribute('for');

        if (forAttr) {
          el.setAttribute(
            'for',
            forAttr.replace(originalSectionId, uniquePrefix)
          );
        }
      });

    // Update variant select IDs
    productElement
      .querySelectorAll('[id^="Variants-"]')
      .forEach((el) => {
        const elId = el.getAttribute('id');

        if (elId && elId.includes(originalSectionId)) {
          el.setAttribute(
            'id',
            elId.replace(originalSectionId, uniquePrefix)
          );
        }
      });

    // Update labels referring to variant IDs
    productElement
      .querySelectorAll('[for^="Variants-"]')
      .forEach((el) => {
        const forAttr = el.getAttribute('for');

        if (forAttr) {
          el.setAttribute(
            'for',
            forAttr.replace(originalSectionId, uniquePrefix)
          );
        }
      });

    // Update data-section attributes
    productElement
      .querySelectorAll('[data-section]')
      .forEach((el) => {
        el.dataset.originalSection = originalSectionId;
        el.dataset.section = uniquePrefix;
      });

    // Update media IDs
    productElement
      .querySelectorAll('[data-media-id]')
      .forEach((el) => {
        const mediaId = el.dataset.mediaId;

        if (
          mediaId &&
          mediaId.includes(originalSectionId)
        ) {
          el.dataset.mediaId = mediaId.replace(
            originalSectionId,
            uniquePrefix
          );
        }
      });

    // Update classes containing old section IDs
    productElement
      .querySelectorAll(`[class*="${originalSectionId}"]`)
      .forEach((el) => {
        el.className = el.className.replace(
          new RegExp(originalSectionId, 'g'),
          uniquePrefix
        );
      });
  }

  /**
   * Inject content and re-execute scripts
   */
  injectContent(container, html) {
    container.innerHTML = html;

    container
      .querySelectorAll('script')
      .forEach((oldScript) => {
        const newScript = document.createElement('script');

        Array.from(oldScript.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value);
        });

        if (oldScript.innerHTML) {
          newScript.appendChild(
            document.createTextNode(oldScript.innerHTML)
          );
        }

        oldScript.parentNode?.replaceChild(
          newScript,
          oldScript
        );
      });
  }

  /**
   * Initialize Shopify features after content injection
   */
  initializeFeatures() {
    if (window.Shopify?.PaymentButton) {
      Shopify.PaymentButton.init();
    }

    if (window.ProductModel) {
      window.ProductModel.loadShopifyXR();
    }

    this.initializeVariantPickers();
  }

  /**
   * Initialize variant pickers loaded via AJAX
   */
  initializeVariantPickers() {
    const container = this.getContentContainer();

    if (!container) return;

    const variantPickers =
      container.querySelectorAll('hooktheme-variant-picker');

    variantPickers.forEach((picker) => {
      if (typeof picker.init === 'function') {
        picker.init();
      } else if (
        typeof picker.connectedCallback === 'function'
      ) {
        try {
          picker.connectedCallback();
        } catch (e) {
          // Ignore duplicate initialization errors
        }
      }
    });

    // Initialize product forms
    const productForms =
      container.querySelectorAll('product-form');

    productForms.forEach((form) => {
      if (typeof form.init === 'function') {
        form.init();
      }
    });

    // Initialize quantity inputs
    const quantityInputs =
      container.querySelectorAll('quantity-input');

    quantityInputs.forEach((input) => {
      if (typeof input.connectedCallback === 'function') {
        try {
          input.connectedCallback();
        } catch (e) {
          // Ignore duplicate initialization errors
        }
      }
    });
  }

  /**
   * Set loading state on trigger element
   */
  setLoading(element, isLoading) {
    if (!element) return;

    if (isLoading) {
      element.setAttribute('aria-disabled', 'true');
      element.classList.add('loading');
    } else {
      element.removeAttribute('aria-disabled');
      element.classList.remove('loading');
    }
  }
}

// Register custom element
customElements.define('hooktheme-quick-view', HookthemeQuickView);

} // End of duplicate declaration prevention check
